import type { UserProfileCountry, UserProfilePreferencesForm, UserProfileState } from "@shared/schemas";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { AlertCircle } from "lucide-react";
import { Controller, type Control, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  setPreferencesViewShippingAddressCardTestId,
  setPreferencesViewShippingAddressDescriptionTestId,
  setPreferencesViewShippingAddressTitleTestId,
} from "../views/preferences-view.utils";
import { RequiredAsterisk } from "./RequiredAsterisk";

export interface ShippingAddressCardProps {
  control: Control<UserProfilePreferencesForm>;
  errors: FieldErrors<UserProfilePreferencesForm>;
  countries: UserProfileCountry[];
  states: UserProfileState[];
  hasStates: boolean;
  isEditable: boolean;
  onCountryChange: (countryCode: string) => void;
  onProvinceChange: (stateCode: string) => void;
  translateValidationError: (key?: string) => string;
}

const cardTestId = setPreferencesViewShippingAddressCardTestId();
const titleTestId = setPreferencesViewShippingAddressTitleTestId();
const descriptionTestId = setPreferencesViewShippingAddressDescriptionTestId();

export const ShippingAddressCard = ({
  control,
  errors,
  countries,
  states,
  hasStates,
  isEditable,
  onCountryChange,
  onProvinceChange,
  translateValidationError,
}: ShippingAddressCardProps) => {
  const { t } = useTranslation();

  return (
    <Card data-testid={cardTestId}>
      <CardHeader>
        <CardTitle data-testid={titleTestId}>{t("views.preferences.shippingAddress.title")}</CardTitle>
        <CardDescription data-testid={descriptionTestId}>{t("views.preferences.shippingAddress.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="shipping-address1">
            {t("views.preferences.shippingAddress.street")}
            <RequiredAsterisk />
          </Label>
          <Controller
            name="shippingAddress.address1"
            control={control}
            render={({ field }) => (
              <Input {...field} id="shipping-address1" aria-invalid={!!errors.shippingAddress?.address1} disabled={!isEditable} />
            )}
          />
          <p className="flex min-h-4 items-center gap-1 text-xs text-destructive">
            {errors.shippingAddress?.address1 && <AlertCircle className="size-3 shrink-0" />}
            {translateValidationError(errors.shippingAddress?.address1?.message as string | undefined)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="shipping-city">
              {t("views.preferences.shippingAddress.city")}
              <RequiredAsterisk />
            </Label>
            <Controller
              name="shippingAddress.city"
              control={control}
              render={({ field }) => (
                <Input {...field} id="shipping-city" aria-invalid={!!errors.shippingAddress?.city} disabled={!isEditable} />
              )}
            />
            <p className="flex min-h-4 items-center gap-1 text-xs text-destructive">
              {errors.shippingAddress?.city && <AlertCircle className="size-3 shrink-0" />}
              {translateValidationError(errors.shippingAddress?.city?.message as string | undefined)}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="shipping-countryCode">
              {t("views.preferences.shippingAddress.country")}
              <RequiredAsterisk />
            </Label>
            <Controller
              name="shippingAddress.countryCode"
              control={control}
              render={({ field }) => (
                <Select value={field.value ?? ""} onValueChange={onCountryChange} disabled={!isEditable}>
                  <SelectTrigger
                    id="shipping-countryCode"
                    className="w-full"
                    aria-invalid={!!(errors.shippingAddress?.countryCode ?? errors.shippingAddress?.country)}
                  >
                    <SelectValue placeholder={t("views.preferences.shippingAddress.selectCountryPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.Name} value={country.CountryCode}>
                        {country.Name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="flex min-h-4 items-center gap-1 text-xs text-destructive">
              {(errors.shippingAddress?.countryCode ?? errors.shippingAddress?.country) && <AlertCircle className="size-3 shrink-0" />}
              {translateValidationError(
                (errors.shippingAddress?.countryCode?.message ?? errors.shippingAddress?.country?.message) as string | undefined
              )}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="shipping-zip">
              {t("views.preferences.shippingAddress.zip")}
              <RequiredAsterisk />
            </Label>
            <Controller
              name="shippingAddress.zip"
              control={control}
              render={({ field }) => (
                <Input {...field} id="shipping-zip" aria-invalid={!!errors.shippingAddress?.zip} disabled={!isEditable} />
              )}
            />
            <p className="flex min-h-4 items-center gap-1 text-xs text-destructive">
              {errors.shippingAddress?.zip && <AlertCircle className="size-3 shrink-0" />}
              {translateValidationError(errors.shippingAddress?.zip?.message as string | undefined)}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="shipping-provinceCode">
              {t("views.preferences.shippingAddress.state")}
              {hasStates && <RequiredAsterisk />}
            </Label>
            <Controller
              name="shippingAddress.provinceCode"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? ""}
                  onValueChange={(value) => value && onProvinceChange(value)}
                  disabled={!isEditable || !hasStates}
                >
                  <SelectTrigger
                    id="shipping-provinceCode"
                    className="w-full"
                    aria-invalid={!!(errors.shippingAddress?.province ?? errors.shippingAddress?.provinceCode)}
                  >
                    <SelectValue placeholder={t("views.preferences.shippingAddress.selectStatePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state.StateCode} value={state.StateCode}>
                        {state.Name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="flex min-h-4 items-center gap-1 text-xs text-destructive">
              {(errors.shippingAddress?.province ?? errors.shippingAddress?.provinceCode) && <AlertCircle className="size-3 shrink-0" />}
              {translateValidationError(
                (errors.shippingAddress?.province?.message ?? errors.shippingAddress?.provinceCode?.message) as string | undefined
              )}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="shipping-latitude">{t("views.preferences.shippingAddress.latitude")}</Label>
            <Controller
              name="shippingAddress.latitude"
              control={control}
              render={({ field }) => <Input {...field} id="shipping-latitude" type="number" value={field.value ?? ""} disabled readOnly />}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="shipping-longitude">{t("views.preferences.shippingAddress.longitude")}</Label>
            <Controller
              name="shippingAddress.longitude"
              control={control}
              render={({ field }) => <Input {...field} id="shipping-longitude" type="number" value={field.value ?? ""} disabled readOnly />}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
